import * as React from "react";

import { Alert } from "modules/common/components/misc";
import { useAppSelector } from "hooks/reduxHooks";
import { useIsMonthEndLockActive, useMonthEndStatus } from "hooks/useMonthEndStatus";
import { Log } from "modules/logging/log";
import { MAINTENANCE_ALERT_MESSAGE, MAINTENANCE_ALERT_TITLE } from "modules/maintenance/messages";
import { MonthEndLockoutSurface } from "modules/maintenance/models";
import { ApplicationState } from "store";

export interface MonthEndMaintenanceAlertProps {
    /**
     * Where this alert is. Supplying it reports a sighting to telemetry the first time the lock is
     * active while this alert is mounted, which is how we tell whether the lockout is actually
     * reaching members and on which screens.
     *
     * Leave it off for an alert that repeats one already counted on the same screen - see
     * MonthEndLockoutSurface for why this is opt-in rather than every alert reporting itself.
     */
    surface?: MonthEndLockoutSurface;
}

/**
 * Explains why the edit controls on a page are disabled during month end, and renders nothing when
 * they are not.
 *
 * Rendering this component is also what subscribes a page to the month-end status - it owns the
 * fetch, so MaintenanceLockButton and SubmitButton elsewhere on the page have something to read.
 * Removing it from a page would silently stop that page from ever locking.
 */
export function MonthEndMaintenanceAlert({ surface }: MonthEndMaintenanceAlertProps): JSX.Element {
    useMonthEndStatus();

    const isMonthEndLockActive = useIsMonthEndLockActive();
    const isEmulatedSession = useAppSelector((state: ApplicationState) => state.auth.session.emulatedSession);

    React.useEffect(() => {
        // Emulated sessions are staff, not members, and staff are most likely to be looking during a
        // month end precisely because a member has already complained - so counting them would
        // inflate the number exactly when it is being read most carefully.
        if (surface && isMonthEndLockActive && !isEmulatedSession) {
            Log.monthEndLockoutShown(surface);
        }
        // Keyed on the lock rather than on every render, so the status refetch on window focus does
        // not report a second sighting of an alert that has been on screen the whole time.
    }, [surface, isMonthEndLockActive, isEmulatedSession]);

    if (!isMonthEndLockActive) {
        return null;
    }

    // "warning" rather than "info": this is a constraint on what the member can do right now, which
    // is what the app already uses warning alerts for (the resubmission and claim editor alerts).
    // It is also the only way to be seen - an info alert with the default colour renders no icon, no
    // border and no background, so it reads as ordinary body text.
    return (
        <Alert variant="warning" title={MAINTENANCE_ALERT_TITLE} fullWidth className="month-end-maintenance-alert">
            {MAINTENANCE_ALERT_MESSAGE}
        </Alert>
    );
}
